import React, { useMemo, useState } from 'react';
import { Bell, ChevronRight, LogOut, MessageCircle, Search, AlertTriangle } from 'lucide-react';
import Sidebar from './Sidebar';
import { useAppContext } from '../context/AppContext';
import { getPageMeta, NAV_ITEMS } from '../data/navigation';
import Chat from './Chat';
import { t } from '../i18n';

const searchIndex = NAV_ITEMS.flatMap((item) =>
  item.children ? item.children.map((child) => ({ id: child.id, label: child.label, moduleLabel: item.label })) : [{ id: item.id, label: item.label, moduleLabel: null }]
);

const Layout = ({
  children,
  currentUser = null,
  currentPage = 'dashboard',
  onNavigate = () => {},
  allowedPages = null,
}) => {
  const {
    notifications,
    markNotificationAsRead,
    removeNotification,
    logout,
    users,
    login,
    renewablePortfolio,
    renewablePermitAlerts,
    purchaseRequisitions,
    purchaseOrders,
    parks,
    categories,
    suppliers,
    projectBudgets,
    preferences,
    updatePreferences
  } = useAppContext();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [globalSearch, setGlobalSearch] = useState('');

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications]
  );

  const pageMeta = getPageMeta(currentPage);
  const availableUsers = useMemo(() => users || [], [users]);
  const selectedParkId = preferences?.projectContext?.parkId || 'all';
  const displayCurrency = preferences?.displayCurrency === 'CLP' ? 'CLP' : 'USD';

  const searchResults = useMemo(() => {
    if (!globalSearch.trim()) return [];
    const term = globalSearch.trim().toLowerCase();
    return searchIndex
      .filter((entry) => !allowedPages || allowedPages.has(entry.id))
      .filter((entry) => entry.label.toLowerCase().includes(term) || t(entry.label).toLowerCase().includes(term))
      .slice(0, 8);
  }, [globalSearch, allowedPages]);

  const warningMessages = useMemo(() => {
    const parksList = Array.isArray(parks) ? parks : [];
    const categoriesList = Array.isArray(categories) ? categories : [];
    const suppliersList = Array.isArray(suppliers) ? suppliers : [];
    const budgetsList = Array.isArray(projectBudgets) ? projectBudgets : [];

    const projectsWithoutPm = parksList.filter(
      (park) => !(park?.team?.projectManagerId || park?.managerId)
    ).length;
    const projectsWithoutCeco = parksList.filter((park) => !Array.isArray(park?.areas) || park.areas.length === 0)
      .length;
    const projectsWithoutWbs = parksList.filter(
      (park) => !Array.isArray(park?.packages) || park.packages.length === 0
    ).length;
    const projectsWithoutBudget = parksList.filter(
      (park) => !budgetsList.find((budget) => budget.parkId === park.id)
    ).length;
    const developmentWithoutCoords = renewablePortfolio.filter((project) => !project.coordinates).length;

    const missingDualApproval = renewablePortfolio.filter(
      (project) => !project.approval?.projectManager || !project.approval?.purchasingManager
    ).length;
    const criticalPermits = renewablePermitAlerts.filter((alert) => alert.priority === 'alta').length;
    const openRequisitions = purchaseRequisitions.filter((req) =>
      ['pendiente_area', 'pendiente_controller', 'pendiente_compra', 'en_licitacion'].includes(req.status)
    ).length;
    const poPendingApproval = purchaseOrders.filter((order) =>
      ['pendiente_pm', 'pendiente_compras'].includes(order.status)
    ).length;
    const messages = [];

    if (parksList.length > 0) {
      if (projectsWithoutPm > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: ${projectsWithoutPm} proyecto(s) no tienen Project Manager asignado. Asigna PM en Administración para habilitar aprobaciones y gobierno del proyecto.`
        );
      }
      if (projectsWithoutCeco > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: ${projectsWithoutCeco} proyecto(s) no tienen Áreas/CECO. Sin CECO no hay imputación ni aprobaciones ordenadas.`
        );
      }
      if (projectsWithoutWbs > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: ${projectsWithoutWbs} proyecto(s) no tienen Paquetes/WBS. Sin WBS no se pueden emitir PR de manera controlada.`
        );
      }
      if (projectsWithoutBudget > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: ${projectsWithoutBudget} proyecto(s) no tienen Presupuesto cargado. Sin presupuesto no se controla sobrepresupuesto por categoría.`
        );
      }
    }

    if (categoriesList.length === 0) {
      messages.push(
        'Usted no puede avanzar sin esto: no hay categorías creadas. Crea categorías y su categoría presupuestaria en Administración.'
      );
    }
    if (suppliersList.length === 0) {
      messages.push(
        'Usted no puede avanzar sin esto: no hay proveedores creados. Crea y aprueba proveedores antes de emitir OCs.'
      );
    }

    if (currentPage.startsWith('development-') || currentPage.startsWith('projects-') || currentPage === 'projects') {
      if (missingDualApproval > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: ${missingDualApproval} proyecto(s) aun no completan aprobacion dual (Gerente de Proyecto + Gerente de Compras).`
        );
      }
      if (criticalPermits > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: ${criticalPermits} permiso(s) o PAS estan en criticidad alta y deben regularizarse antes de cerrar etapa.`
        );
      }
      if (developmentWithoutCoords > 0) {
        messages.push(
          `Recomendación: ${developmentWithoutCoords} proyecto(s) en Desarrollo no tienen coordenadas (lat/lng). Sin coordenadas el mapa no centra correctamente.`
        );
      }
    }

    if (currentPage.startsWith('procurement-')) {
      if (openRequisitions > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: hay ${openRequisitions} PR abiertas; la PO requiere PR aprobada y vinculada al proyecto.`
        );
      }
      if (poPendingApproval > 0) {
        messages.push(
          `Usted no puede avanzar sin esto: hay ${poPendingApproval} OC pendientes de aprobacion (Gerente Proyecto + Gerente Compras).`
        );
      }
    }

    return messages;
  }, [
    currentPage,
    renewablePortfolio,
    renewablePermitAlerts,
    purchaseRequisitions,
    purchaseOrders,
    parks,
    categories,
    suppliers,
    projectBudgets
  ]);

  const handleUserSwitch = (event) => {
    const nextEmail = event.target.value;
    if (!nextEmail || nextEmail === currentUser?.email) return;
    const selectedUser = availableUsers.find((user) => user.email === nextEmail);
    const password = selectedUser?.password || 'admin123';
    login(nextEmail, password);
  };

  const handleParkSwitch = (event) => {
    updatePreferences({ projectContext: { parkId: event.target.value } });
  };

  const handleCurrencySwitch = (event) => {
    updatePreferences({ displayCurrency: event.target.value });
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      <Sidebar
        currentUser={currentUser}
        currentPage={currentPage}
        onNavigate={onNavigate}
        allowedPages={allowedPages}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((prev) => !prev)}
      />

      <div className={`flex-1 flex flex-col min-h-screen relative ${sidebarCollapsed ? 'ml-14' : 'ml-60'}`}>
        <header className="bg-white border-b border-neutral-200 px-4 h-12 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <div className="relative w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
              <input
                type="text"
                value={globalSearch}
                onChange={(event) => setGlobalSearch(event.target.value)}
                placeholder={t('Buscar módulo o página...')}
                className="w-full h-7 pl-8 pr-2 text-sm border border-neutral-200 rounded bg-neutral-50 focus:outline-none focus:ring-1 focus:ring-brand-400 focus:bg-white"
              />
              {searchResults.length > 0 && (
                <div className="absolute left-0 top-8 w-72 bg-white border border-neutral-200 rounded shadow-lg z-50">
                  {searchResults.map((result) => (
                    <button
                      key={result.id}
                      onClick={() => { onNavigate(result.id); setGlobalSearch(''); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-neutral-50 flex items-center justify-between"
                    >
                      <span className="text-neutral-800">{t(result.label)}</span>
                      {result.moduleLabel && <span className="text-xs text-neutral-400">{t(result.moduleLabel)}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center gap-1 text-xs text-neutral-500 min-w-0">
              {pageMeta.moduleLabel && (
                <>
                  <span className="truncate">{t(pageMeta.moduleLabel)}</span>
                  <ChevronRight className="w-3 h-3 shrink-0" />
                </>
              )}
              <span className="font-medium text-neutral-800 truncate">{t(pageMeta.pageLabel)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {Array.isArray(parks) && parks.length > 0 && (
              <select
                className="h-7 px-2 text-xs border border-neutral-200 rounded bg-white text-neutral-600 focus:outline-none focus:ring-1 focus:ring-brand-400"
                value={selectedParkId}
                onChange={handleParkSwitch}
                aria-label={t('Proyecto / parque')}
              >
                <option value="all">{t('Todos los proyectos')}</option>
                {parks.map((park) => (
                  <option key={park.id} value={park.id}>{t(park.name)}</option>
                ))}
              </select>
            )}

            <select
              className="h-7 px-2 text-xs border border-neutral-200 rounded bg-white text-neutral-600 focus:outline-none focus:ring-1 focus:ring-brand-400"
              value={displayCurrency}
              onChange={handleCurrencySwitch}
              aria-label={t('Moneda')}
            >
              <option value="USD">{t('USD')}</option>
              <option value="CLP">{t('CLP')}</option>
            </select>

            <div className="w-px h-5 bg-neutral-200 mx-1" />

            <div className="relative">
              <button
                className="relative p-1.5 rounded hover:bg-neutral-100"
                onClick={() => setNotificationsOpen((prev) => !prev)}
                aria-label={t('Notificaciones')}
              >
                <Bell className="w-4 h-4 text-neutral-500" />
                {unreadCount > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-danger-500 text-white text-[10px] leading-none font-semibold rounded-full w-3.5 h-3.5 flex items-center justify-center">
                    {t(unreadCount)}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 top-9 w-80 bg-white shadow-lg rounded border border-neutral-200 overflow-hidden z-50">
                  <div className="px-3 py-2 border-b border-neutral-100 flex items-center justify-between">
                    <p className="text-sm font-semibold text-neutral-700">{t('Notificaciones')}</p>
                    <span className="text-xs text-neutral-400">{t(unreadCount)} {t('sin leer')}</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto divide-y divide-neutral-100">
                    {notifications.length === 0 && (
                      <div className="p-4 text-sm text-neutral-500 text-center">{t('No tienes notificaciones recientes')}</div>
                    )}
                    {notifications.map((notification) => (
                      <div key={notification.id} className="p-3 hover:bg-neutral-50">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-neutral-800">{t(notification.title)}</h4>
                          <span className="text-xs text-neutral-400">{t(notification.createdAt)}</span>
                        </div>
                        <p className="text-sm text-neutral-600 mt-0.5">{t(notification.message)}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          {!notification.read && (
                            <button
                              onClick={() => markNotificationAsRead(notification.id)}
                              className="text-xs font-medium text-brand-600 hover:text-brand-700"
                            >{t('Marcar como leída')}</button>
                          )}
                          <button
                            onClick={() => removeNotification(notification.id)}
                            className="text-xs font-medium text-neutral-400 hover:text-danger-600"
                          >{t('Ocultar')}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="w-px h-5 bg-neutral-200 mx-1" />

            <div className="relative">
              <select
                className="h-7 pl-1 pr-1 text-xs text-neutral-600 bg-transparent focus:outline-none cursor-pointer max-w-[140px]"
                value={currentUser?.email || ''}
                onChange={handleUserSwitch}
                aria-label={t('Cambiar usuario')}
              >
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.email}>{t(user.name)}</option>
                ))}
              </select>
            </div>

            <button
              className="flex items-center gap-1.5 h-7 px-2 text-xs font-medium text-neutral-500 hover:text-danger-600 hover:bg-danger-50 rounded"
              onClick={logout}
            >
              <LogOut className="w-3.5 h-3.5" />{t('Salir')}</button>
          </div>
        </header>

        <main className="flex-1 p-5">
          {warningMessages.length > 0 && (
            <div className="mb-3 space-y-1.5">
              {warningMessages.map((message) => (
                <div
                  key={message}
                  className="rounded border border-warning-100 bg-warning-50 text-warning-700 px-3 py-2 text-xs flex items-start gap-2"
                >
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>{t(message)}</span>
                </div>
              ))}
            </div>
          )}
          {children}
        </main>

        <button
          className="fixed z-40 bottom-5 right-5 bg-brand-600 hover:bg-brand-700 text-white rounded-full p-3 flex items-center justify-center shadow-md focus:outline-none"
          onClick={() => setChatOpen(true)}
          aria-label={t('Abrir chat')}
        >
          <MessageCircle className="w-4 h-4" />
        </button>

        {chatOpen && (
          <div className="fixed z-50 bottom-16 right-5 w-[340px] max-w-[95vw]">
            <div className="bg-white rounded shadow-lg border border-neutral-200 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 bg-neutral-50">
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-3.5 h-3.5 text-neutral-500" />
                  <span className="font-semibold text-neutral-700 text-sm">{t('Chat')}</span>
                </div>
                <button
                  className="text-neutral-400 hover:text-danger-600 text-sm px-1"
                  onClick={() => setChatOpen(false)}
                  aria-label={t('Cerrar chat')}
                >
                  &#10005;
                </button>
              </div>
              <Chat />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;
